import React, { useState } from "react";
import { getSupabase } from "../supabaseClient";
import { ORDERS_COLOR } from "../../utils/metadataColors";

/**
 * Download a stored report via the signed-URL endpoint.
 *
 * get-view-url now requires a VERIFIED bearer token and enforces a tenant check
 * (see ai_assistant.get_fax_view_url). The token goes in the Authorization header,
 * never the URL — a JWT in a query string leaks into logs and browser history.
 */
async function fetchDownloadUrl(filePath) {
  const { BASE_URLS } = await import("../env");
  const backendUrl =
    window.location.hostname === "localhost" ? BASE_URLS.local.backend : BASE_URLS.render.backend;

  const supabase = await getSupabase();
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error("You appear to be signed out. Please sign in again.");

  const url =
    `${backendUrl}/api/fax/get-view-url/?file_path=${encodeURIComponent(filePath)}&download=1`;

  const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) {
    let message = "Could not get a download link";
    try {
      message = (await response.json()).error || message;
    } catch (_e) {
      /* keep the default */
    }
    if (response.status === 403) message = "You do not have access to this file";
    throw new Error(message);
  }
  // The returned signed URL is itself authorised, so window.open needs no header.
  return (await response.json()).url;
}

export default function ReportDownloadButton({ filePath, filename, disabled = false, onError }) {
  const [busy, setBusy] = useState(false);

  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (busy || disabled) return;
    setBusy(true);
    try {
      const url = await fetchDownloadUrl(filePath);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      if (onError) onError(err);
      else console.error("Download failed:", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || busy}
      aria-label={`Download ${filename}`}
      title={`Download ${filename}`}
      style={{
        fontSize: "11px",
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: "4px",
        border: `1px solid ${ORDERS_COLOR}`,
        color: ORDERS_COLOR,
        backgroundColor: "transparent",
        cursor: disabled || busy ? "not-allowed" : "pointer",
        opacity: disabled || busy ? 0.5 : 1,
      }}
    >
      {busy ? "…" : "⬇ Download"}
    </button>
  );
}
